import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} BulkOrderRefs
 * @property {HTMLElement} tierSelector - The tier selector container
 * @property {HTMLElement} tierCount - The tier count display
 * @property {HTMLElement} tierNext - The next tier message element
 * @property {HTMLElement} variantsList - The variants list container
 * @property {HTMLInputElement[]} quantityInputs - Array of quantity input elements
 * @property {HTMLButtonElement} submitButton - The submit button
 * @property {HTMLElement} submitText - The submit button text
 * @property {HTMLInputElement|null} preorderConfirmCheckbox - Preorder confirmation checkbox (if block requires it)
 * @property {HTMLElement|null} preorderGateWrap - Gate container
 * @property {HTMLElement|null} preorderGateContent - Gate content wrapper
 * @property {HTMLInputElement|null} preorderGateCheckbox - Gate confirmation checkbox
 * @property {HTMLButtonElement|null} preorderGateButton - Gate continue button
 * @property {HTMLInputElement|null} preorderGateDatePicker - Gate date picker (bulk preorder gate)
 * @property {HTMLElement} errorMessage - The error message element
 */

/**
 * @typedef {Object} PricingTier
 * @property {number} min - Minimum quantity for this tier
 * @property {number|null} max - Maximum quantity (null = unlimited)
 * @property {number} price - Price per unit at this tier
 * @property {string} label - Display label for the tier
 */

/**
 * A custom element that manages bulk order functionality with tier-based pricing.
 * Tracks quantities per variant, calculates tier progress, and handles multi-item cart submission.
 * 
 * Supports hybrid cart operations:
 * - Tracks current cart quantities separately from pending (desired) quantities
 * - Shows "Update Cart" when there are pending changes
 * - Uses /cart/add.js for new items and /cart/update.js for existing items
 *
 * @extends {Component<BulkOrderRefs>}
 */
export class BulkOrderComponent extends Component {
  /**
   * Map of variant ID to PENDING quantity (what user wants)
   * @type {Map<string, number>}
   */
  quantities = new Map();

  /**
   * Map of variant ID to CART quantity (what's currently in cart)
   * @type {Map<string, number>}
   */
  cartQuantities = new Map();

  /**
   * Array of pricing tiers
   * @type {PricingTier[]}
   */
  tiers = [];

  /**
   * Whether the component is currently loading
   * @type {boolean}
   */
  isLoading = false;

  /**
   * Currency symbol for price display
   * @type {string}
   */
  currencySymbol = '$';

  /**
   * Locale strings for display
   * @type {Record<string, string>}
   */
  locale = {};

  /**
   * Whether the preorder gate screen is enabled
   * @type {boolean}
   */
  preorderGateEnabled = false;

  /**
   * Whether the preorder gate has been confirmed
   * @type {boolean}
   */
  preorderGateConfirmed = false;

  /**
   * Selected order ready date from gate date picker
   * @type {string|null}
   */
  selectedOrderDate = null;

  /**
   * Store-rendered current date (YYYY-MM-DD) used for date calculations.
   * @type {string|null}
   */
  storeToday = null;

  /**
   * Lead days before an order can be marked ready.
   * @type {number}
   */
  gateLeadDays = 7;

  /**
   * Allowed weekdays for ready dates. 0 = Sunday, 6 = Saturday.
   * @type {Set<number>}
   */
  gateAllowedWeekdays = new Set();

  /**
   * Blackout dates that should be rejected even if they match weekday rules.
   * @type {Set<string>}
   */
  gateBlackoutDates = new Set();

  /**
   * Maximum allowed gate date (YYYY-MM-DD). Only relevant when using a <input type="date">.
   * @type {string|null}
   */
  gateMaxDate = null;

  /**
   * Earliest selectable gate date after lead time, weekday rules, and blackout dates.
   * @type {string|null}
   */
  gateEarliestDate = null;

  /**
   * Read-only progress fill element inside the tier track.
   * @type {HTMLElement|null}
   */
  tierProgressFill = null;

  /**
   * Normalizes a ref value to always be an array
   * @param {HTMLElement|HTMLElement[]|null|undefined} ref - The ref value
   * @returns {HTMLElement[]} Array of elements (empty if ref is null/undefined)
   */
  #normalizeRefToArray(ref) {
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }

  /**
   * Normalizes a date string to YYYY-MM-DD.
   *
   * Accepts:
   * - YYYY-MM-DD (or YYYY-M-D)
   * - YYYY/MM/DD (or YYYY/M/D)
   * - MM/DD/YYYY (or M/D/YYYY) as a convenience (US-style)
   *
   * @param {string} value
   * @returns {string|null}
   */
  normalizeYmd(value) {
    if (typeof value !== 'string') return null;
    const raw = value.trim();
    if (!raw) return null;

    // YYYY-MM-DD or YYYY/MM/DD (allow 1-2 digit month/day)
    let m = raw.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
      return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }

    // MM/DD/YYYY (US-style convenience)
    m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const mo = Number(m[1]);
      const d = Number(m[2]);
      const y = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
      return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }

    return null;
  }

  /**
   * Formats today's local date as YYYY-MM-DD.
   * @returns {string}
   */
  getTodayYmd() {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }

  /**
   * Converts a YYYY-MM-DD string to a local Date instance.
   * @param {string} value
   * @returns {Date|null}
   */
  ymdToLocalDate(value) {
    const normalized = this.normalizeYmd(value);
    if (!normalized) return null;

    const [year, month, day] = normalized.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Adds whole days to a YYYY-MM-DD date string.
   * @param {string} value
   * @param {number} days
   * @returns {string}
   */
  addDaysToYmd(value, days) {
    const date = this.ymdToLocalDate(value) || new Date();
    date.setDate(date.getDate() + days);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  /**
   * Formats a YYYY-MM-DD string for customer-facing messages.
   * @param {string} value
   * @returns {string}
   */
  formatYmdLong(value) {
    const date = this.ymdToLocalDate(value);
    if (!date) return value;

    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Parses a CSV-style weekday list from a data attribute.
   * @param {string} value
   * @returns {Set<number>}
   */
  parseWeekdayList(value) {
    const weekdays = new Set();

    value
      .split(/[\s,|]+/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const day = Number.parseInt(entry, 10);
        if (Number.isInteger(day) && day >= 0 && day <= 6) {
          weekdays.add(day);
        }
      });

    return weekdays;
  }

  /**
   * Parses a delimited blackout date list into normalized YYYY-MM-DD values.
   * @param {string} value
   * @returns {Set<string>}
   */
  parseBlackoutDates(value) {
    const dates = new Set();

    value
      .split(/[|,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const normalized = this.normalizeYmd(entry);
        if (normalized) {
          dates.add(normalized);
        }
      });

    return dates;
  }

  /**
   * Formats a locale template by replacing {{ token }} placeholders.
   * @param {string} template
   * @param {Record<string, string|number>} replacements
   * @returns {string}
   */
  formatLocaleString(template, replacements = {}) {
    if (!template) return '';

    let output = template;

    Object.entries(replacements).forEach(([key, value]) => {
      output = output.replaceAll(`[${key}]`, String(value));
    });

    return output;
  }

  /**
   * Returns whether the supplied date falls on an allowed weekday.
   * @param {string} value
   * @returns {boolean}
   */
  isGateWeekdayAllowed(value) {
    const date = this.ymdToLocalDate(value);
    if (!date || this.gateAllowedWeekdays.size === 0) return false;
    return this.gateAllowedWeekdays.has(date.getDay());
  }

  /**
   * Finds the first valid ready date starting from the supplied floor date.
   * @param {string} startYmd
   * @returns {string|null}
   */
  findFirstValidGateDate(startYmd) {
    const normalizedStart = this.normalizeYmd(startYmd);
    if (!normalizedStart || this.gateAllowedWeekdays.size === 0) {
      return null;
    }

    let candidate = normalizedStart;
    for (let i = 0; i < 730; i += 1) {
      if (this.gateMaxDate && candidate > this.gateMaxDate) {
        return null;
      }

      if (this.isGateWeekdayAllowed(candidate) && !this.gateBlackoutDates.has(candidate)) {
        return candidate;
      }

      candidate = this.addDaysToYmd(candidate, 1);
    }

    return null;
  }

  /**
   * Formats the allowed weekday list for an error message.
   * @returns {string}
   */
  formatAllowedWeekdays() {
    if (this.gateAllowedWeekdays.size === 0) {
      return 'none';
    }

    const formatter = new Intl.DateTimeFormat(undefined, { weekday: 'long' });
    const labels = Array.from(this.gateAllowedWeekdays)
      .sort((a, b) => a - b)
      .map((day) => {
        const baseDate = new Date(2026, 0, 4 + day);
        return formatter.format(baseDate);
      });

    return labels.join(', ');
  }

  /**
   * Returns a validation message for a selected gate date, or null when valid.
   * @param {string} value
   * @returns {string|null}
   */
  getGateDateValidationMessage(value) {
    const normalized = this.normalizeYmd(value);
    if (!normalized) {
      return 'Please select a valid order ready date.';
    }

    if (!this.gateEarliestDate) {
      return 'No order ready dates are currently available. Please contact us.';
    }

    if (normalized < this.gateEarliestDate) {
      return `Please select ${this.formatYmdLong(this.gateEarliestDate)} or later.`;
    }

    if (this.gateMaxDate && normalized > this.gateMaxDate) {
      return `Please select ${this.formatYmdLong(this.gateMaxDate)} or earlier.`;
    }

    if (!this.isGateWeekdayAllowed(normalized)) {
      return `Please choose one of the available order days: ${this.formatAllowedWeekdays()}.`;
    }

    if (this.gateBlackoutDates.has(normalized)) {
      return `${this.formatYmdLong(normalized)} is unavailable. Please choose another date.`;
    }

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Parse tiers from data attribute
    const tiersJson = this.dataset.tiers;
    if (tiersJson) {
      try {
        this.tiers = JSON.parse(tiersJson);
        // Sort tiers by min quantity
        this.tiers.sort((a, b) => a.min - b.min);
        // Generate labels for tiers that don't have them
        this.generateTierLabels();
      } catch (error) {
        console.error('Failed to parse pricing tiers:', error);
        this.tiers = [];
      }
    }

    // Get currency symbol
    this.currencySymbol = this.dataset.currencySymbol || '$';

    // Get locale strings
    this.locale = {
      items: this.dataset.localeItems || 'items',
      itemsCount: this.dataset.localeItemsCount || '[count] items',
      nextTier: this.dataset.localeNextTier || '[count] more for [label]',
      maxTier: this.dataset.localeMaxTier || 'Maximum discount unlocked!',
      minimumTier: this.dataset.localeMinimumTier || 'Add [count] items to meet the minimum order.',
      discountApplied: this.dataset.localeDiscountApplied || '[label] applied',
      noDiscount: this.dataset.localeNoDiscount || 'Standard pricing',
      addToCart: this.dataset.localeAddToCart || 'Add to Cart',
      updateCart: this.dataset.localeUpdateCart || 'Update Cart',
      adding: this.dataset.localeAdding || 'Updating...',
      added: this.dataset.localeAdded || 'Updated',
      error: this.dataset.localeError || 'Error updating cart',
      preorderConfirmationRequired: this.dataset.localePreorderConfirmationRequired || 'Please confirm that you understand this is a preorder and there is a minimum order.',
    };

    // Preorder confirmation (optional block setting)
    this.requirePreorderConfirmation = this.dataset.requirePreorderConfirmation === 'true';
    this.preorderGateEnabled = this.dataset.preorderGateEnabled === 'true';

    // Date scheduling rules for the preorder gate
    const configuredTodayRaw = (this.dataset.scheduleToday || '').trim();
    const configuredLeadDaysRaw = (this.dataset.scheduleLeadDays || '').trim();
    const configuredWeekdaysRaw = (this.dataset.scheduleAllowedWeekdays || '').trim();
    const configuredBlackoutsRaw = (this.dataset.scheduleBlackoutDates || '').trim();
    const configuredMaxRaw = (this.dataset.scheduleMaxDate || '').trim();

    this.storeToday = this.normalizeYmd(configuredTodayRaw) || this.getTodayYmd();
    this.gateLeadDays = Math.max(0, Number.parseInt(configuredLeadDaysRaw, 10) || 0);
    this.gateAllowedWeekdays = this.parseWeekdayList(configuredWeekdaysRaw);
    this.gateBlackoutDates = this.parseBlackoutDates(configuredBlackoutsRaw);
    this.gateMaxDate = this.normalizeYmd(configuredMaxRaw);
    if (configuredTodayRaw && !this.normalizeYmd(configuredTodayRaw)) {
      console.warn('[bulk-order] Invalid scheduleToday (expected YYYY-MM-DD):', configuredTodayRaw);
    }
    if (configuredMaxRaw && !this.gateMaxDate) {
      console.warn('[bulk-order] Invalid scheduleMaxDate (expected YYYY-MM-DD):', configuredMaxRaw);
    }
    if (this.gateMaxDate && this.gateMaxDate < this.storeToday) {
      console.warn(
        '[bulk-order] Ignoring scheduleMaxDate because it is earlier than scheduleToday:',
        this.storeToday,
        this.gateMaxDate
      );
      this.gateMaxDate = null;
    }

    // Initialize quantity inputs (reads from DOM which has Liquid-rendered cart values)
    this.initializeQuantityInputs();

    // Set up event listeners
    this.setupEventListeners();
    this.setupPreorderGate();

    // Listen for cart updates to sync quantities
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Render tier selector (also sets up slider interaction)
    this.renderTierSelector();

    // Try to sync from window.Shopify.cart if available
    this.#syncCartQuantitiesFromGlobal();

    // Initial update
    this.updateUI();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListeners();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles cart update events to sync cart quantities
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Skip if this component triggered the event
    if (event.target === this) return;

    const cart = event.detail?.resource;
    if (cart?.items) {
      this.#syncCartQuantitiesFromData(cart);
    }
  };

  /**
   * Syncs cart quantities from the global Shopify cart object
   */
  #syncCartQuantitiesFromGlobal() {
    // @ts-ignore - Shopify namespace may not be typed
    const cart = window.Shopify?.cart;
    if (cart?.items) {
      this.#syncCartQuantitiesFromData(cart);
    }
  }

  /**
   * Syncs cart quantities from cart data (updates cartQuantities map)
   * Also updates pending quantities to match cart if no pending changes
   * @param {Object} cart - The cart object
   */
  #syncCartQuantitiesFromData(cart) {
    const quantityInputs = this.#normalizeRefToArray(this.refs.quantityInputs);
    if (!quantityInputs.length) return;

    // Build map of variant ID to cart quantity
    const newCartQuantities = new Map();
    for (const item of cart.items) {
      const variantId = item.variant_id.toString();
      const existing = newCartQuantities.get(variantId) || 0;
      newCartQuantities.set(variantId, existing + item.quantity);
    }

    // Update cart quantities and sync pending quantities
    quantityInputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      if (variantId) {
        const cartQty = newCartQuantities.get(variantId) || 0;
        const oldCartQty = this.cartQuantities.get(variantId) || 0;
        const pendingQty = this.quantities.get(variantId) || 0;

        // Update cart quantity
        this.cartQuantities.set(variantId, cartQty);

        // If pending quantity matched old cart quantity, update it to new cart quantity
        // This handles external cart changes (e.g., from cart drawer)
        if (pendingQty === oldCartQty) {
          this.quantities.set(variantId, cartQty);
          input.value = cartQty.toString();
          this.updateQuantityButtons(variantId, cartQty, input);
        }
      }
    });

    this.updateUI();
  }

  /**
   * Generates labels for tiers that don't have them
   * Calculates discount percentage based on base price (first tier)
   */
  generateTierLabels() {
    if (!this.tiers.length) return;

    const basePrice = this.tiers[0].price;

    this.tiers.forEach((tier, index) => {
      if (index === 0) {
        tier.label = 'Min';
        return;
      }

      if (!tier.label) {
        const discountPercent = Math.round(((basePrice - tier.price) / basePrice) * 100);
        if (discountPercent > 0) {
          tier.label = `${discountPercent}% Off`;
        } else {
          tier.label = `${tier.min}+ Items`;
        }
      }
    });
  }

  /**
   * Initializes quantity inputs and sets up their event listeners
   * Reads initial values from DOM (which are rendered by Liquid with cart quantities)
   */
  initializeQuantityInputs() {
    const quantityInputs = this.#normalizeRefToArray(this.refs.quantityInputs);
    if (!quantityInputs.length) return;

    quantityInputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      if (variantId) {
        // Read initial value from DOM (Liquid renders cart quantity)
        const currentValue = parseInt(input.value) || 0;
        
        // Initialize both pending and cart quantities to the same value
        this.quantities.set(variantId, currentValue);
        this.cartQuantities.set(variantId, currentValue);

        // Set up input change handler
        input.addEventListener('change', () => this.handleQuantityChange(input));
        input.addEventListener('input', () => this.handleQuantityInput(input));

        // Set up button handlers (supports both pill and row layouts)
        const container = input.closest('.bulk-order__pill') || input.closest('.bulk-order__variant-row');
        if (container) {
          const minusBtn = container.querySelector('[data-action="decrease"]');
          const plusBtn = container.querySelector('[data-action="increase"]');

          if (minusBtn) {
            minusBtn.addEventListener('click', () => this.decreaseQuantity(variantId, input));
          }
          if (plusBtn) {
            plusBtn.addEventListener('click', () => this.increaseQuantity(variantId, input));
          }
        }
      }
    });
  }

  /**
   * Sets up event listeners for quantity changes
   */
  setupEventListeners() {
    const { submitButton, preorderConfirmCheckbox } = this.refs;
    if (submitButton) {
      submitButton.addEventListener('click', () => this.handleSubmit());
    }
    if (preorderConfirmCheckbox) {
      preorderConfirmCheckbox.addEventListener('change', () => {
        const { errorMessage } = this.refs;
        if (preorderConfirmCheckbox.checked && errorMessage) {
          errorMessage.textContent = '';
          errorMessage.classList.add('visually-hidden');
        }
        this.updateSubmitButton();
      });
    }
  }

  /**
   * Sets up the preorder gate screen
   */
  setupPreorderGate() {
    const { preorderGateWrap, preorderGateContent, preorderGateDatePicker, preorderGateButton } = this.refs;

    if (!this.preorderGateEnabled || !preorderGateContent) {
      this.preorderGateConfirmed = true;
      if (preorderGateWrap) preorderGateWrap.hidden = true;
      if (preorderGateContent) preorderGateContent.hidden = false;
      return;
    }

    this.preorderGateConfirmed = false;

    const scheduleFloorDate = this.addDaysToYmd(this.storeToday || this.getTodayYmd(), this.gateLeadDays);
    this.gateEarliestDate = this.findFirstValidGateDate(scheduleFloorDate);

    // Set up date picker with computed scheduling constraints
    if (preorderGateDatePicker) {
      if (preorderGateDatePicker instanceof HTMLInputElement && preorderGateDatePicker.type === 'date') {
        if (this.gateEarliestDate) {
          preorderGateDatePicker.min = this.gateEarliestDate;
        } else {
          preorderGateDatePicker.removeAttribute('min');
        }

        if (this.gateMaxDate) {
          preorderGateDatePicker.max = this.gateMaxDate;
        } else {
          preorderGateDatePicker.removeAttribute('max');
        }
      }

      preorderGateDatePicker.value = '';
      preorderGateDatePicker.disabled = !this.gateEarliestDate;

      preorderGateDatePicker.addEventListener('change', () => {
        this.validateGateDate();
        this.updatePreorderGateUI();
      });
    }

    if (preorderGateButton) {
      preorderGateButton.addEventListener('click', () => {
        if (!this.validateGateDate()) return;
        this.preorderGateConfirmed = true;
        this.updatePreorderGateUI();
        this.updateSubmitButton();
      });
    }

    if (!this.gateEarliestDate) {
      this.selectedOrderDate = null;
      this.setGateDateError('No order ready dates are currently available. Please contact us.');
    }

    this.updatePreorderGateUI();
  }

  /**
   * Validates the gate date picker value against the computed scheduling rules.
   * Resets invalid dates and shows an error hint.
   * @returns {boolean} Whether the date is valid
   */
  validateGateDate() {
    const { preorderGateDatePicker } = this.refs;
    if (!preorderGateDatePicker) return true;

    if (!this.gateEarliestDate) {
      this.selectedOrderDate = null;
      this.setGateDateError('No order ready dates are currently available. Please contact us.');
      return false;
    }

    const value = preorderGateDatePicker.value;
    if (!value) {
      this.selectedOrderDate = null;
      this.setGateDateError(null);
      return false;
    }

    const validationMessage = this.getGateDateValidationMessage(value);
    if (validationMessage) {
      preorderGateDatePicker.value = '';
      this.selectedOrderDate = null;
      this.setGateDateError(validationMessage);
      return false;
    }

    this.selectedOrderDate = value;
    this.setGateDateError(null);
    return true;
  }

  /**
   * Shows or clears the date picker error message
   * @param {string|null} message - Error message, or null to clear
   */
  setGateDateError(message) {
    const { preorderGateDatePicker } = this.refs;
    if (!preorderGateDatePicker) return;

    const container = preorderGateDatePicker.closest('.bulk-order__gate-date');
    if (!container) return;

    let errorEl = container.querySelector('.bulk-order__gate-date-error');

    if (message) {
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'bulk-order__gate-date-error';
        container.appendChild(errorEl);
      }
      errorEl.textContent = message;
      preorderGateDatePicker.classList.add('bulk-order__gate-date-input--invalid');
    } else {
      if (errorEl) errorEl.remove();
      preorderGateDatePicker.classList.remove('bulk-order__gate-date-input--invalid');
    }
  }

  /**
   * Updates preorder gate UI and button state
   */
  updatePreorderGateUI() {
    const { preorderGateWrap, preorderGateContent, preorderGateDatePicker, preorderGateButton } = this.refs;

    if (!this.preorderGateEnabled) {
      if (preorderGateWrap) preorderGateWrap.hidden = true;
      if (preorderGateContent) preorderGateContent.hidden = false;
      this.preorderGateConfirmed = true;
      return;
    }

    const hasValidDate =
      preorderGateDatePicker
        ? Boolean(preorderGateDatePicker.value) &&
          !this.getGateDateValidationMessage(preorderGateDatePicker.value)
        : Boolean(this.gateEarliestDate);
    if (preorderGateButton) {
      preorderGateButton.disabled = !hasValidDate;
    }

    if (preorderGateWrap) {
      preorderGateWrap.hidden = this.preorderGateConfirmed;
    }
    if (preorderGateContent) {
      preorderGateContent.hidden = !this.preorderGateConfirmed;
    }
  }

  /**
   * Removes event listeners
   */
  removeEventListeners() {
    // Event listeners are automatically cleaned up when elements are removed
  }

  /**
   * Handles quantity input change event (on blur/enter)
   * @param {HTMLInputElement} input - The quantity input element
   */
  handleQuantityChange(input) {
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    let value = parseInt(input.value) || 0;
    const max = input.max ? parseInt(input.max) : null;
    const min = parseInt(input.min) || 0;
    const firstTierMin = this.tiers[0]?.min || 1;

    // Per-variant minimum: if value is between 1 and minimum-1, jump to minimum
    if (firstTierMin > 1 && value > 0 && value < firstTierMin) {
      value = firstTierMin;
    }

    // Clamp value to valid range
    let clampedValue = Math.max(min, value);
    if (max !== null && max !== '') {
      clampedValue = Math.min(clampedValue, parseInt(max));
    }

    if (clampedValue !== parseInt(input.value)) {
      input.value = clampedValue.toString();
    }

    // Update pending quantity (not cart)
    this.quantities.set(variantId, clampedValue);
    this.updateQuantityButtons(variantId, clampedValue, input);
    this.updateUI();
  }

  /**
   * Handles quantity input input event (for real-time updates while typing)
   * @param {HTMLInputElement} input - The quantity input element
   */
  handleQuantityInput(input) {
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const value = parseInt(input.value) || 0;
    this.quantities.set(variantId, value);
    this.updateUI();
  }

  /**
   * Increases pending quantity for a variant (does NOT update cart)
   * @param {string} variantId - The variant ID
   * @param {HTMLInputElement} input - The quantity input element
   */
  increaseQuantity(variantId, input) {
    const current = parseInt(input.value) || 0;
    const max = input.max ? parseInt(input.max) : null;
    const firstTierMin = this.tiers[0]?.min || 1;

    // If variant is at 0, jump to tier minimum (per-variant minimum)
    if (current === 0 && firstTierMin > 1) {
      const newValue = max !== null && max !== '' ? Math.min(firstTierMin, parseInt(max)) : firstTierMin;
      input.value = newValue.toString();
      this.quantities.set(variantId, newValue);
      this.updateQuantityButtons(variantId, newValue, input);
      this.updateUI();
      return;
    }

    let newValue = current + 1;
    if (max !== null && max !== '') {
      newValue = Math.min(newValue, parseInt(max));
    }

    input.value = newValue.toString();
    this.quantities.set(variantId, newValue);
    this.updateQuantityButtons(variantId, newValue, input);
    this.updateUI();
  }

  /**
   * Decreases pending quantity for a variant (does NOT update cart)
   * @param {string} variantId - The variant ID
   * @param {HTMLInputElement} input - The quantity input element
   */
  decreaseQuantity(variantId, input) {
    const current = parseInt(input.value) || 0;
    const min = parseInt(input.min) || 0;
    const firstTierMin = this.tiers[0]?.min || 1;

    // Per-variant minimum: if at or below minimum, jump to 0 (remove item)
    if (firstTierMin > 1 && current <= firstTierMin) {
      input.value = '0';
      this.quantities.set(variantId, 0);
      this.updateQuantityButtons(variantId, 0, input);
      this.updateUI();
      return;
    }

    const newValue = Math.max(min, current - 1);
    input.value = newValue.toString();
    this.quantities.set(variantId, newValue);
    this.updateQuantityButtons(variantId, newValue, input);
    this.updateUI();
  }

  /**
   * Updates quantity button states
   * @param {string} variantId - The variant ID
   * @param {number} quantity - The current pending quantity
   * @param {HTMLInputElement} input - The quantity input element
   */
  updateQuantityButtons(variantId, quantity, input) {
    // Support both pill and row layouts
    const container = input.closest('.bulk-order__pill') || input.closest('.bulk-order__variant-row');
    if (!container) return;

    const minusBtn = container.querySelector('[data-action="decrease"]');
    const plusBtn = container.querySelector('[data-action="increase"]');
    const min = parseInt(input.min) || 0;
    const max = input.max ? parseInt(input.max) : null;

    if (minusBtn) {
      // Disable minus only at 0 (at minimum, clicking minus jumps to 0)
      minusBtn.disabled = quantity <= min;
    }

    if (plusBtn) {
      plusBtn.disabled = max !== null && max !== '' && quantity >= parseInt(max);
    }
  }

  /**
   * Calculates the total pending quantity across all variants
   * @returns {number} Total pending quantity
   */
  getTotalQuantity() {
    let total = 0;
    for (const quantity of this.quantities.values()) {
      total += quantity;
    }
    return total;
  }

  /**
   * Calculates the total cart quantity across all variants
   * @returns {number} Total cart quantity
   */
  getTotalCartQuantity() {
    let total = 0;
    for (const quantity of this.cartQuantities.values()) {
      total += quantity;
    }
    return total;
  }

  /**
   * Checks if there are any pending changes (differences between pending and cart)
   * @returns {boolean} True if there are pending changes
   */
  hasPendingChanges() {
    for (const [variantId, pendingQty] of this.quantities.entries()) {
      const cartQty = this.cartQuantities.get(variantId) || 0;
      if (pendingQty !== cartQty) {
        return true;
      }
    }
    return false;
  }

  /**
   * Gets the changes between pending and cart quantities
   * @returns {{ toAdd: Array<{id: string, quantity: number}>, toUpdate: Array<{id: string, quantity: number}> }}
   */
  getChanges() {
    const toAdd = [];
    const toUpdate = [];

    for (const [variantId, pendingQty] of this.quantities.entries()) {
      const cartQty = this.cartQuantities.get(variantId) || 0;
      
      if (pendingQty !== cartQty) {
        if (cartQty === 0 && pendingQty > 0) {
          // Item not in cart, need to add
          toAdd.push({ id: variantId, quantity: pendingQty });
        } else {
          // Item in cart, need to update (including setting to 0 to remove)
          toUpdate.push({ id: variantId, quantity: pendingQty });
        }
      }
    }

    return { toAdd, toUpdate };
  }

  /**
   * Gets the net change in quantity (for display)
   * @returns {number} Net change (positive = adding, negative = removing)
   */
  getNetChange() {
    let netChange = 0;
    for (const [variantId, pendingQty] of this.quantities.entries()) {
      const cartQty = this.cartQuantities.get(variantId) || 0;
      netChange += (pendingQty - cartQty);
    }
    return netChange;
  }

  /**
   * Gets the current pricing tier based on total quantity
   * @param {number} totalQuantity - Total quantity across all variants
   * @returns {PricingTier|null} The current tier, or null if no tiers
   */
  getCurrentTier(totalQuantity) {
    if (!this.tiers.length) return null;

    // Find the highest tier that the quantity qualifies for
    let currentTier = null;
    for (const tier of this.tiers) {
      if (totalQuantity >= tier.min) {
        if (tier.max === null || totalQuantity <= tier.max) {
          currentTier = tier;
        }
      }
    }

    // If quantity exceeds all tiers, return the highest tier
    if (!currentTier && this.tiers.length > 0) {
      const highestTier = this.tiers[this.tiers.length - 1];
      if (totalQuantity >= highestTier.min && highestTier.max === null) {
        return highestTier;
      }
    }

    return currentTier;
  }

  /**
   * Gets the next pricing tier
   * @param {number} totalQuantity - Total quantity across all variants
   * @returns {PricingTier|null} The next tier, or null if at max tier
   */
  getNextTier(totalQuantity) {
    if (!this.tiers.length) return null;

    const currentTier = this.getCurrentTier(totalQuantity);
    if (!currentTier) return this.tiers[0];

    const currentIndex = this.tiers.indexOf(currentTier);
    if (currentIndex < this.tiers.length - 1) {
      return this.tiers[currentIndex + 1];
    }

    // Check if current tier has a max and we can reach next tier
    if (currentTier.max !== null) {
      const nextTier = this.tiers.find((t) => t.min > currentTier.max);
      return nextTier || null;
    }

    return null; // At maximum tier
  }

  /**
   * Gets the index of the current tier
   * @param {number} totalQuantity - Total quantity across all variants
   * @returns {number} The index of the current tier, or -1 if none
   */
  getCurrentTierIndex(totalQuantity) {
    if (!this.tiers.length) return -1;

    let currentIndex = -1;
    for (let i = 0; i < this.tiers.length; i++) {
      const tier = this.tiers[i];
      if (totalQuantity >= tier.min) {
        if (tier.max === null || totalQuantity <= tier.max) {
          currentIndex = i;
        }
      }
    }

    // If quantity exceeds all tiers, return the highest tier index
    if (currentIndex === -1 && this.tiers.length > 0) {
      const highestTier = this.tiers[this.tiers.length - 1];
      if (totalQuantity >= highestTier.min && highestTier.max === null) {
        return this.tiers.length - 1;
      }
    }

    return currentIndex;
  }

  /**
   * Returns the segment boundaries used by the tier bar.
   * Format: [0, ...tier minimums, synthetic end]
   * @returns {number[]}
   */
  getTierBoundaries() {
    if (!this.tiers.length) return [0, 1];

    const minimums = this.tiers.map((tier) => Math.max(Number(tier.min) || 0, 0));
    const lastMin = minimums[minimums.length - 1] || 0;
    const previousMin = minimums[minimums.length - 2] || 0;
    const lastGap = minimums.length > 1
      ? Math.max(lastMin - previousMin, 1)
      : Math.max(lastMin, 1);

    return [0, ...minimums, lastMin + lastGap];
  }

  /**
   * Returns the synthetic tier bar maximum.
   * @returns {number}
   */
  getTierBarMaximum() {
    const boundaries = this.getTierBoundaries();
    return boundaries[boundaries.length - 1] || 1;
  }

  /**
   * Returns a 0-100 percentage for a quantity on the tier bar scale.
   * @param {number} quantity
   * @returns {number}
   */
  getTierBarPercent(quantity) {
    const max = this.getTierBarMaximum();
    const clampedQuantity = Math.max(0, Number(quantity) || 0);

    if (clampedQuantity >= max) return 100;

    return (clampedQuantity / Math.max(max, 1)) * 100;
  }

  /**
   * Returns the customer-facing label for a tier.
   * @param {PricingTier} tier
   * @returns {string}
   */
  formatTierDisplayLabel(tier) {
    return `${tier.min}+ ${tier.label}`;
  }

  /**
   * Returns progress percentage for a tier row in the tablet/mobile list.
   * Previous tiers are full, future tiers are empty, and the current tier fills within its range.
   * @param {number} totalQuantity
   * @param {number} tierIndex
   * @returns {number}
   */
  getTierRowProgressPercent(totalQuantity, tierIndex) {
    if (!this.tiers.length || tierIndex < 0 || tierIndex >= this.tiers.length) return 0;

    const quantity = Math.max(0, Number(totalQuantity) || 0);
    const boundaries = this.getTierBoundaries();
    const tier = this.tiers[tierIndex];
    const nextBoundary = boundaries[tierIndex + 2] || this.getTierBarMaximum();

    if (tierIndex === 0) {
      const firstThreshold = Math.max(tier.min, 1);

      if (quantity < tier.min) {
        return Math.min((quantity / firstThreshold) * 100, 100);
      }
    }

    if (quantity < tier.min) {
      return 0;
    }

    if (quantity >= nextBoundary) {
      return 100;
    }

    return Math.min(((quantity - tier.min) / Math.max(nextBoundary - tier.min, 1)) * 100, 100);
  }

  /**
   * Renders the segmented tier selector
   */
  renderTierSelector() {
    const { tierSelector, tierTrack, tierMarkers } = this.refs;
    if (!tierSelector || !tierTrack || !this.tiers.length) return;

    tierTrack.innerHTML = '';
    if (tierMarkers) {
      tierMarkers.innerHTML = '';
    }

    this.tierProgressFill = null;

    // Hide the progress UI when there is only one tier. The summary bar still renders.
    if (this.tiers.length < 2) {
      tierSelector.hidden = true;
      return;
    }

    tierSelector.hidden = false;

    const body = document.createElement('div');
    body.className = 'bulk-order__tier-body';
    tierTrack.appendChild(body);

    const fill = document.createElement('div');
    fill.className = 'bulk-order__tier-progress';
    body.appendChild(fill);
    this.tierProgressFill = fill;

    const boundaries = this.getTierBoundaries();

    this.tiers.forEach((tier, index) => {
      const tierMin = boundaries[index + 1];
      const divider = document.createElement('div');
      divider.className = 'bulk-order__tier-divider';
      divider.dataset.tierIndex = index.toString();
      const dividerPosition = this.getTierBarPercent(tierMin);
      divider.style.left = `${dividerPosition}%`;
      body.appendChild(divider);

      if (index === 0) {
        const minimumAnchor = document.createElement('div');
        minimumAnchor.className = 'bulk-order__tier-anchor bulk-order__tier-state';
        minimumAnchor.dataset.tierIndex = '0';
        minimumAnchor.dataset.tierMin = tier.min.toString();
        minimumAnchor.style.left = `${dividerPosition}%`;
        if (dividerPosition <= 8) {
          minimumAnchor.classList.add('bulk-order__tier-anchor--edge-start');
        }

        const label = document.createElement('span');
        label.className = 'bulk-order__tier-anchor-text';
        label.textContent = this.formatTierDisplayLabel(tier);

        minimumAnchor.appendChild(label);
        tierTrack.appendChild(minimumAnchor);
        return;
      }

      const nextBoundary = boundaries[index + 2] || this.getTierBarMaximum();
      const segmentLeft = dividerPosition;
      const segmentRight = this.getTierBarPercent(nextBoundary);
      const segment = document.createElement('div');
      segment.className = 'bulk-order__tier-segment-label bulk-order__tier-state';
      segment.dataset.tierIndex = index.toString();
      segment.dataset.tierMin = tier.min.toString();
      segment.style.left = `${segmentLeft}%`;
      segment.style.width = `${Math.max(segmentRight - segmentLeft, 0)}%`;

      const label = document.createElement('span');
      label.className = 'bulk-order__tier-segment-label-text';
      label.textContent = this.formatTierDisplayLabel(tier);

      segment.appendChild(label);
      body.appendChild(segment);
    });

    this.renderTierMarkers();
  }

  /**
   * Renders the mobile tier list.
   */
  renderTierMarkers() {
    const { tierMarkers } = this.refs;
    if (!tierMarkers) return;

    tierMarkers.innerHTML = '';
    tierMarkers.setAttribute('role', 'list');

    this.tiers.forEach((tier, index) => {
      const marker = document.createElement('div');
      marker.className = 'bulk-order__tier-list-item bulk-order__tier-state';
      marker.dataset.tierIndex = index.toString();
      marker.dataset.tierMin = tier.min.toString();
      marker.setAttribute('role', 'listitem');

      const progress = document.createElement('div');
      progress.className = 'bulk-order__tier-list-progress';
      marker.appendChild(progress);

      const label = document.createElement('span');
      label.className = 'bulk-order__tier-list-label';
      label.textContent = this.formatTierDisplayLabel(tier);

      marker.appendChild(label);
      tierMarkers.appendChild(marker);
    });
  }

  /**
   * Updates the tier selector UI and summary details.
   */
  updateTierSelector() {
    const totalQuantity = this.getTotalQuantity();
    const currentTier = this.getCurrentTier(totalQuantity);
    const nextTier = this.getNextTier(totalQuantity);
    const basePrice = this.tiers[0]?.price || 0;

    this.updateSegmentFills(totalQuantity);
    this.updateTierMarkers(totalQuantity);
    this.updateSummaryBar(totalQuantity, currentTier, nextTier, basePrice);
  }

  /**
   * Updates the fill percentage for each segment
   * @param {number} totalQuantity - Total quantity
   */
  updateSegmentFills(totalQuantity) {
    if (!this.tierProgressFill) return;

    const widthPercent = this.getTierBarPercent(totalQuantity);
    this.tierProgressFill.style.width = `${widthPercent}%`;
    this.tierProgressFill.classList.toggle('is-complete', widthPercent >= 99.9);
  }

  /**
   * Updates tier marker reached/current states.
   * @param {number} totalQuantity - Total quantity
   */
  updateTierMarkers(totalQuantity) {
    const currentTierIndex = this.getCurrentTierIndex(totalQuantity);
    const markers = this.querySelectorAll('.bulk-order__tier-state[data-tier-index]');

    markers.forEach((marker) => {
      const tierIndex = Number.parseInt(marker.dataset.tierIndex || '-1', 10);
      const tierMin = Number.parseInt(marker.dataset.tierMin || '0', 10);

      marker.classList.toggle('is-reached', totalQuantity >= tierMin);
      marker.classList.toggle('is-current', tierIndex === currentTierIndex);

      if (marker.classList.contains('bulk-order__tier-list-item')) {
        const progress = marker.querySelector('.bulk-order__tier-list-progress');
        if (progress) {
          const widthPercent = this.getTierRowProgressPercent(totalQuantity, tierIndex);
          progress.style.width = `${widthPercent}%`;
          progress.classList.toggle('is-complete', widthPercent >= 99.9);
        }
      }
    });
  }

  /**
   * Updates the summary bar content.
   * @param {number} totalQuantity - Total quantity
   * @param {PricingTier|null} currentTier - Current tier
   * @param {PricingTier|null} nextTier - Next tier
   * @param {number} basePrice - Base price (first tier)
   */
  updateSummaryBar(totalQuantity, currentTier, nextTier, basePrice) {
    const { summaryCount, summaryDiscount, summaryTotalPrice } = this.refs;
    const firstTier = this.tiers[0];

    if (summaryCount) {
      summaryCount.textContent = this.formatLocaleString(this.locale.itemsCount, {
        count: totalQuantity,
      });
    }

    if (summaryDiscount) {
      if (firstTier && totalQuantity < firstTier.min) {
        summaryDiscount.textContent = this.formatLocaleString(this.locale.minimumTier, {
          count: Math.max(firstTier.min - totalQuantity, 0),
          label: firstTier.label,
        });
      } else if (nextTier) {
        summaryDiscount.textContent = this.formatLocaleString(this.locale.nextTier, {
          count: Math.max(nextTier.min - totalQuantity, 0),
          label: nextTier.label,
        });
      } else if (currentTier && currentTier.price < basePrice) {
        summaryDiscount.textContent = this.formatLocaleString(this.locale.discountApplied, {
          label: currentTier.label,
        });
      } else if (currentTier) {
        summaryDiscount.textContent = this.locale.maxTier;
      } else {
        summaryDiscount.textContent = this.locale.noDiscount;
      }
    }

    if (summaryTotalPrice) {
      summaryTotalPrice.textContent = this.formatPrice(this.calculateTotalPrice());
    }
  }

  /**
   * Calculates total price based on current tier pricing
   * @returns {number} Total price in cents
   */
  calculateTotalPrice() {
    const totalQuantity = this.getTotalQuantity();
    const currentTier = this.getCurrentTier(totalQuantity);

    // Tier prices are in dollars, need to convert to cents
    // If no tier, fallback to variant prices
    if (!currentTier && this.tiers.length === 0) {
      return this.calculatePriceFromVariants();
    }

    // Use tier price (in dollars) for all variants
    const tierPriceInCents = (currentTier?.price || this.tiers[0]?.price || 0) * 100;
    return this.calculatePriceForTier(tierPriceInCents);
  }

  /**
   * Calculates price using tier pricing (same price per unit for all variants)
   * @param {number} pricePerUnitInCents - Price per unit in cents
   * @returns {number} Total price in cents
   */
  calculatePriceForTier(pricePerUnitInCents) {
    let totalPrice = 0;
    for (const quantity of this.quantities.values()) {
      totalPrice += pricePerUnitInCents * quantity;
    }
    return totalPrice;
  }

  /**
   * Calculates price using individual variant prices (fallback when no tiers)
   * @returns {number} Total price in cents
   */
  calculatePriceFromVariants() {
    let totalPrice = 0;
    const quantityInputs = this.#normalizeRefToArray(this.refs.quantityInputs);

    quantityInputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      const qty = this.quantities.get(variantId) || 0;

      if (qty > 0) {
        const container = input.closest('.bulk-order__pill') || input.closest('.bulk-order__variant-row');
        const variantPrice = container?.dataset.variantPrice
          ? parseInt(container.dataset.variantPrice, 10)
          : 0; // Variant prices are already in cents

        totalPrice += variantPrice * qty;
      }
    });

    return totalPrice;
  }

  /**
   * Formats price for display
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return `${this.currencySymbol}${price.toFixed(2)}`;
  }

  /**
   * Updates the submit button state and text
   */
  updateSubmitButton() {
    const { submitButton, submitText, preorderConfirmCheckbox } = this.refs;

    if (!submitButton) return;

    const hasPendingChanges = this.hasPendingChanges();
    const preorderConfirmed = !this.requirePreorderConfirmation ||
      !preorderConfirmCheckbox ||
      preorderConfirmCheckbox.checked;
    const gateConfirmed = !this.preorderGateEnabled || this.preorderGateConfirmed;

    // Button is enabled if there are pending changes and (if required) preorder is confirmed
    submitButton.disabled = !hasPendingChanges || this.isLoading || !preorderConfirmed || !gateConfirmed;

    // Update button text: "Update Cart (n items)" when pending changes and total > 0 (wireframe)
    if (submitText) {
      if (this.isLoading) {
        submitText.textContent = this.locale.adding;
      } else if (hasPendingChanges) {
        const totalItems = this.getTotalQuantity();
        submitText.textContent = totalItems > 0
          ? `${this.locale.updateCart} (${totalItems} ${this.locale.items})`
          : this.locale.updateCart;
      } else {
        submitText.textContent = this.locale.addToCart;
      }
    }
  }

  /**
   * Updates all UI elements
   */
  updateUI() {
    this.updateTierSelector();
    this.updateSubmitButton();
    this.updateAllQuantityButtons();
  }

  /**
   * Updates all quantity button states (needed when tier minimum affects all buttons)
   */
  updateAllQuantityButtons() {
    const quantityInputs = this.#normalizeRefToArray(this.refs.quantityInputs);
    quantityInputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      if (variantId) {
        const quantity = this.quantities.get(variantId) || 0;
        this.updateQuantityButtons(variantId, quantity, input);
      }
    });
  }

  /**
   * Handles submit button click - updates cart with pending changes
   */
  async handleSubmit() {
    if (this.isLoading) return;
    if (this.preorderGateEnabled && !this.preorderGateConfirmed) return;

    const { preorderConfirmCheckbox, errorMessage } = this.refs;
    if (this.requirePreorderConfirmation && preorderConfirmCheckbox && !preorderConfirmCheckbox.checked) {
      if (errorMessage) {
        errorMessage.textContent = this.locale.preorderConfirmationRequired;
        errorMessage.classList.remove('visually-hidden');
      }
      return;
    }

    const { toAdd, toUpdate } = this.getChanges();
    
    if (toAdd.length === 0 && toUpdate.length === 0) return;

    this.isLoading = true;
    this.classList.add('bulk-order--loading');
    this.updateSubmitButton();

    // Clear any previous errors
    if (errorMessage) {
      errorMessage.textContent = '';
      errorMessage.classList.add('visually-hidden');
    }

    try {
      let cartData = null;

      // First, handle updates to existing items (including removals)
      if (toUpdate.length > 0) {
        const formData = new FormData();
        for (const { id, quantity } of toUpdate) {
          formData.append(`updates[${id}]`, quantity.toString());
        }

        const updateResponse = await fetch(Theme.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
          },
          body: formData,
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || this.locale.error);
        }

        cartData = await updateResponse.json();
      }

      // Then, handle new items to add
      if (toAdd.length > 0) {
        // Add order date property if selected
        const itemsWithProperties = toAdd.map(item => {
          if (this.selectedOrderDate) {
            return {
              ...item,
              properties: {
                'Order Ready Date': this.selectedOrderDate,
              },
            };
          }
          return item;
        });

        const addResponse = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ items: itemsWithProperties }),
        });

        if (!addResponse.ok) {
          const errorData = await addResponse.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || this.locale.error);
        }

        // cart/add.js returns the added items, not the full cart
        // We need to fetch the full cart to get accurate state
        const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { 'Accept': 'application/json' },
        });
        
        if (cartResponse.ok) {
          cartData = await cartResponse.json();
        }
      }

      // Update global cart reference
      if (cartData && typeof window !== 'undefined') {
        // @ts-ignore
        window.Shopify = window.Shopify || {};
        // @ts-ignore
        window.Shopify.cart = cartData;
      }

      // Update cart quantities to match pending quantities (they're now synced)
      for (const [variantId, pendingQty] of this.quantities.entries()) {
        this.cartQuantities.set(variantId, pendingQty);
      }

      // Dispatch cart update event
      const itemCount = cartData?.item_count;
      const sourceId = this.id || this.getAttribute('id') || this.dataset.sectionId || 'bulk-order-component';

      this.dispatchEvent(
        new CartUpdateEvent(cartData, sourceId, {
          itemCount,
          source: 'bulk-order-component',
          productId: this.dataset.productId,
        })
      );

      // Show success feedback
      const { submitText: submitTextRef } = this.refs;
      if (submitTextRef) {
        const originalText = this.locale.addToCart;
        submitTextRef.textContent = this.locale.added;
        setTimeout(() => {
          if (submitTextRef) {
            this.updateSubmitButton();
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to update cart:', error);

      // Show error message
      if (errorMessage) {
        errorMessage.textContent = error.message || this.locale.error;
        errorMessage.classList.remove('visually-hidden');
      }

      // Dispatch cart error event
      const sourceId = this.id || this.getAttribute('id') || this.dataset.sectionId || 'bulk-order-component';
      this.dispatchEvent(
        new CartErrorEvent(sourceId, error.message || this.locale.error, error.description, error.errors)
      );
    } finally {
      this.isLoading = false;
      this.classList.remove('bulk-order--loading');
      this.updateSubmitButton();
    }
  }

  /**
   * Resets all pending quantities to match cart quantities
   */
  resetToCartQuantities() {
    const quantityInputs = this.#normalizeRefToArray(this.refs.quantityInputs);
    quantityInputs.forEach((input) => {
      const variantId = input.dataset.variantId;
      if (variantId) {
        const cartQty = this.cartQuantities.get(variantId) || 0;
        input.value = cartQty.toString();
        this.quantities.set(variantId, cartQty);
        this.updateQuantityButtons(variantId, cartQty, input);
      }
    });
    this.updateUI();
  }
}

if (!customElements.get('bulk-order-component')) {
  customElements.define('bulk-order-component', BulkOrderComponent);
}
